import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { VehicleFormDialog } from "@/components/VehicleFormDialog";
import { VehicleCard } from "@/components/VehicleCard";
import { useAuth } from "@/hooks/useAuth";
import { useUserVehicles } from "@/hooks/useUserVehicles";
import { Car, Plus } from "lucide-react";

interface GarageStepProps {
  onNext: () => void;
}

export const GarageStep = ({ onNext }: GarageStepProps) => {
  const { user } = useAuth();
  const { vehicles, createVehicleAsync, updateVehicle, deleteVehicle, setFeaturedVehicle, isLoading } = useUserVehicles(user?.id);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleAddVehicle = () => {
    setIsDialogOpen(true);
  };

  const handleCreateVehicle = async (data: any) => {
    try {
      const isFirstVehicle = !vehicles || vehicles.length === 0;
      const newVehicle = await createVehicleAsync({
        ...data,
        is_featured: isFirstVehicle, // Auto-feature first vehicle
      });
      setIsDialogOpen(false);
    } catch (error) {
      // Error is handled by the hook's onError
    }
  };

  const hasVehicles = vehicles && vehicles.length > 0;

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Car className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-2xl">Add Your Rig</CardTitle>
        <CardDescription>
          Show off what you drive! Add your vehicle to your profile and associate it with your runs.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {hasVehicles ? (
          <div className="space-y-4">
            {vehicles.map((vehicle) => (
              <VehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                onUpdate={(data) => updateVehicle({ vehicleId: vehicle.id, ...data })}
                onDelete={() => deleteVehicle(vehicle.id)}
                onSetFeatured={() => setFeaturedVehicle(vehicle.id)}
              />
            ))}
            
            <Button
              variant="outline"
              onClick={handleAddVehicle}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Vehicle
            </Button>
          </div>
        ) : (
          <div className="text-center py-8 bg-muted/30 rounded-lg border-2 border-dashed">
            <Car className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              No vehicles added yet
            </p>
            <Button onClick={handleAddVehicle}>
              <Plus className="w-4 h-4 mr-2" />
              Add My First Vehicle
            </Button>
          </div>
        )}

        <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
          <p className="font-medium mb-2">Why add your vehicle?</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Show off your rig on your profile</li>
            <li>Associate vehicles with runs</li>
            <li>Connect with similar rigs in the community</li>
          </ul>
        </div>

        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={onNext}
            className="flex-1"
            size="lg"
          >
            {hasVehicles ? "Continue" : "Skip for Now"}
          </Button>
          {hasVehicles && (
            <Button 
              onClick={onNext}
              className="flex-1"
              size="lg"
            >
              Continue
            </Button>
          )}
        </div>
      </CardContent>

      {user?.id && (
        <VehicleFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onSubmit={handleCreateVehicle}
          userId={user.id}
        />
      )}
    </Card>
  );
};
